'use client';

import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { DynamicForm } from '@/features/forms';
import { cn } from '@/lib/utils/tw-merge';
import { useDictionary } from '@/components/providers';
import type { ResolvedFormTab } from './TabForm';

type PrivacyPolicyLink = { slug?: string; title?: string; language?: string };

const TAB_CLASS =
    'flex-1 cursor-pointer whitespace-nowrap rounded-lg px-4 py-[9px] text-sm font-medium transition-colors max-[560px]:whitespace-normal max-[560px]:text-center';

/** Client-Tabs: der aktive Tab kommt aus dem URL-Hash (= Formular-ID) und lässt
 *  sich per Klick wechseln. Deep-Links (auch aus Navigation/Footer) aktivieren
 *  den passenden Tab und scrollen sanft zum Block. Jeder Tab zeigt optional
 *  einen eigenen Text über dem Formular. */
export function TabFormTabs({
    tabs,
    privacyPolicyPage,
}: {
    tabs: ResolvedFormTab[];
    privacyPolicyPage?: PrivacyPolicyLink;
}) {
    const { a11y } = useDictionary();
    const rootRef = useRef<HTMLDivElement>(null);
    const [active, setActive] = useState(tabs[0]?.form.id ?? '');

    const scrollToBlock = useCallback(() => {
        const section = rootRef.current?.closest('section');
        if (!section) return;
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        section.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    }, []);

    // Stabiler Effekt-Trigger (Repo-Muster wie useScrollSpy) statt der bei jedem
    // Render neuen tabs-Referenz.
    const formIds = tabs.map((tab) => tab.form.id).join('|');

    useEffect(() => {
        const ids = new Set(formIds.split('|'));

        // Aktiviert den zum URL-Hash (= Formular-ID) passenden Tab und scrollt zum Block.
        const activateFromHash = () => {
            const hash = decodeURIComponent(window.location.hash.slice(1));
            if (hash && ids.has(hash)) {
                setActive(hash);
                scrollToBlock();
            }
        };

        activateFromHash(); // Deep-Link beim Laden / nach Cross-Page-Navigation

        // Manuelle URL-Änderung, echte <a>-Navigation, Zurück/Vorwärts.
        window.addEventListener('hashchange', activateFromHash);
        window.addEventListener('popstate', activateFromHash);

        // Next.js <Link> wechselt bei gleicher Route per history.pushState und löst
        // KEIN hashchange aus (deshalb reagierte der Footer-Link bisher nicht).
        // Solche Klicks in der Capture-Phase abfangen und den Tab direkt am
        // Link-Ziel aktivieren – unabhängig davon, wie Next intern navigiert.
        const onDocumentClick = (event: MouseEvent) => {
            const link = (event.target as Element | null)?.closest('a[href]');
            if (!(link instanceof HTMLAnchorElement)) return;
            if (link.pathname !== window.location.pathname) return; // andere Seite → dortiger Mount-Handler
            const targetId = decodeURIComponent(link.hash.slice(1));
            if (targetId && ids.has(targetId)) {
                setActive(targetId);
                requestAnimationFrame(scrollToBlock);
            }
        };
        document.addEventListener('click', onDocumentClick, true);

        return () => {
            window.removeEventListener('hashchange', activateFromHash);
            window.removeEventListener('popstate', activateFromHash);
            document.removeEventListener('click', onDocumentClick, true);
        };
    }, [formIds, scrollToBlock]);

    // replaceState statt Hash-Zuweisung → kein erneutes Scrollen/hashchange bei Klick.
    const selectTab = (id: string) => {
        setActive(id);
        history.replaceState(null, '', `#${id}`);
    };

    return (
        <div ref={rootRef}>
            <div
                role="tablist"
                aria-label={a11y.forms}
                className="mx-auto mb-10 flex w-fit min-w-[min(560px,100%)] max-w-140 gap-1 rounded-xl bg-surface-2 p-1 max-[560px]:w-full max-[560px]:flex-col"
            >
                {tabs.map((tab) => {
                    const selected = tab.form.id === active;
                    return (
                        <button
                            key={tab.form.id}
                            id={`tab-${tab.form.id}`}
                            type="button"
                            role="tab"
                            aria-selected={selected}
                            aria-controls={`panel-${tab.form.id}`}
                            onClick={() => selectTab(tab.form.id)}
                            className={cn(
                                TAB_CLASS,
                                selected
                                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                                    : 'text-copy-2 hover:text-title',
                            )}
                        >
                            {tab.form.title}
                        </button>
                    );
                })}
            </div>

            <div className="relative mx-auto max-w-180 overflow-hidden rounded-2xl border border-hairline bg-page p-(--space-card-48) shadow-[0_1px_3px_rgba(0,0,0,0.04),0_4px_20px_rgba(0,0,0,0.06)]">
                {tabs.map((tab) => (
                    <div
                        key={tab.form.id}
                        role="tabpanel"
                        id={`panel-${tab.form.id}`}
                        aria-labelledby={`tab-${tab.form.id}`}
                        hidden={tab.form.id !== active}
                    >
                        <Suspense fallback={null}>
                            <DynamicForm
                                form={tab.form}
                                privacyPolicyPage={privacyPolicyPage}
                                content={tab.content}
                                contentClassName="mb-(--space-intro-48)"
                                successText={tab.successText}
                                errorText={tab.errorText}
                                framed
                            />
                        </Suspense>
                    </div>
                ))}
            </div>
        </div>
    );
}
